import { useState } from "react";
import {
  Text,
  View,
  Pressable,
  ActivityIndicator,
  Switch,
} from "react-native";
import KeyboardAwareScroll from "@/components/KeyboardAwareScroll";
import styled from "styled-components/native";
import { useRouter } from "expo-router";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import { createChannel } from "@/lib/services";
import { colors, radius, font } from "@/lib/theme";
import ContentWidth from "@/components/ContentWidth";
import type { ChannelVisibility } from "@/lib/types";

const EMOJI_OPTIONS = ["💬", "🏏", "📋", "🎉", "💰", "🏆", "📢", "🍺", "⚡", "🎯"];

const Section = styled.View`
  padding: 20px;
`;

const SectionLabel = styled.Text`
  font-size: 12px;
  font-weight: ${font.extrabold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
`;

const Card = styled.View`
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border: 1px solid ${colors.line};
  padding: 16px;
`;

const Label = styled.Text`
  font-size: 11px;
  font-weight: ${font.bold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
  margin-top: 16px;
`;

const Input = styled.TextInput`
  background-color: ${colors.bg};
  border: 1px solid ${colors.line};
  border-radius: ${radius.md}px;
  padding: 12px 14px;
  font-size: 15px;
  color: ${colors.ink};
`;

const EmojiRow = styled.View`
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const ToggleRow = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-vertical: 12px;
`;

const ToggleLabel = styled.Text`
  font-size: 15px;
  font-weight: ${font.semibold};
  color: ${colors.ink};
  flex: 1;
`;

const ToggleHint = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
  margin-top: 2px;
`;

const ErrorText = styled.Text`
  color: ${colors.danger};
  font-size: 14px;
  font-weight: ${font.semibold};
  margin-top: 12px;
`;

const SaveBtn = styled.View`
  background-color: ${colors.brand};
  border-radius: ${radius.card}px;
  padding: 16px;
  align-items: center;
  margin: 20px;
`;

const SaveBtnText = styled.Text`
  color: #fff;
  font-size: 16px;
  font-weight: ${font.bold};
`;

const Spacer = styled.View`
  height: 32px;
`;

export default function CreateChannelScreen() {
  const router = useRouter();
  const { user } = useAuth();
  const { club, membership } = useClub();

  const [name, setName] = useState("");
  const [emoji, setEmoji] = useState("💬");
  const [isPrivate, setIsPrivate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleCreate = async () => {
    if (!name.trim()) {
      setError("Channel name is required.");
      return;
    }
    if (!user || !club || !membership) return;

    setSaving(true);
    setError("");

    try {
      const channelId = await createChannel(club.id, {
        name: name.trim(),
        emoji,
        visibility: isPrivate ? "private" : "open",
        createdBy: user.uid,
        createdByName: membership.displayName,
      });

      router.back();
    } catch (err: any) {
      setError(err.message || "Something went wrong.");
      setSaving(false);
    }
  };

  return (
    <KeyboardAwareScroll style={{ flex: 1, backgroundColor: colors.bg }} showsVerticalScrollIndicator={false}>
        <ContentWidth>
          <Section>
            <SectionLabel>New channel</SectionLabel>
            <Card>
              <Label style={{ marginTop: 0 }}>Channel name</Label>
              <Input
                placeholder="e.g. 1st XI, Committee, Social"
                placeholderTextColor={colors.inkMuted}
                value={name}
                onChangeText={setName}
                autoCapitalize="words"
                autoFocus
              />

              <Label>Icon</Label>
              <EmojiRow>
                {EMOJI_OPTIONS.map((e) => (
                  <Pressable
                    key={e}
                    onPress={() => setEmoji(e)}
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: emoji === e ? colors.brand50 : colors.bg,
                      borderWidth: 2,
                      borderColor: emoji === e ? colors.brand : colors.line,
                    }}
                  >
                    <Text style={{ fontSize: 20 }}>{e}</Text>
                  </Pressable>
                ))}
              </EmojiRow>
            </Card>
          </Section>

          <Section>
            <SectionLabel>Visibility</SectionLabel>
            <Card>
              <ToggleRow>
                <View style={{ flex: 1 }}>
                  <ToggleLabel>Private channel</ToggleLabel>
                  <ToggleHint>
                    {isPrivate
                      ? "Only invited members can see and join"
                      : "Anyone in the club can see and join"}
                  </ToggleHint>
                </View>
                <Switch
                  value={isPrivate}
                  onValueChange={setIsPrivate}
                  trackColor={{ false: colors.line, true: colors.brandLight }}
                  thumbColor={isPrivate ? colors.brand : "#fff"}
                />
              </ToggleRow>
            </Card>
          </Section>

          {error ? (
            <Section>
              <ErrorText>{error}</ErrorText>
            </Section>
          ) : null}

          <Pressable
            style={({ pressed }) => ({ opacity: pressed ? 0.9 : 1 })}
            onPress={handleCreate}
            disabled={saving}
          >
            <SaveBtn>
              {saving ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <SaveBtnText>Create channel</SaveBtnText>
              )}
            </SaveBtn>
          </Pressable>

          <Spacer />
        </ContentWidth>
    </KeyboardAwareScroll>
  );
}
